import { Button } from '@/components/ui/atoms/button'
import { ButtonGoogle } from '@/components/ui/atoms/button-google'
import { ButtonLoading } from '@/components/ui/atoms/button-loading'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/atoms/form'
import { Input } from '@/components/ui/atoms/input'
import { toast } from '@/components/ui/molecules/use-toast'
import { GOOGLE_CLIENT_KEY, TOKEN_MARKUP } from '@/consts'
import { logger, navigate, open } from '@/lib/client'
import { AccountSignUpSchema } from '@/schemas/account'
import { actions } from '@/store/authentication'
import { SignUp } from '@/tracking/authentication'
import type { IAccount, IAuthentication } from '@/types/account'
import { zodResolver } from '@hookform/resolvers/zod'
import { useStore } from '@nanostores/react'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { Eye, EyeOff } from 'lucide-react'
import { atom } from 'nanostores'
import type * as React from 'react'
import { useForm } from 'react-hook-form'
import type * as z from 'zod'

const seePasswordAtom = atom<boolean>(false)
const seePasswordConfirmationAtom = atom<boolean>(false)
const loadingAtom = atom<'idle' | 'loading' | 'succeed' | 'failed'>('idle')

export default function AccountFormSignUp({
  source,
  tokenize = false,
}: { source?: string; tokenize?: boolean }) {
  const $seePassword = useStore(seePasswordAtom)
  const $seePasswordConfirmation = useStore(seePasswordConfirmationAtom)

  const form = useForm<z.infer<typeof AccountSignUpSchema>>({
    resolver: zodResolver(AccountSignUpSchema),
    defaultValues: {
      name: '',
      email: '',
    },
  })

  const initSession = (json: IAccount & IAuthentication) => {
    if (!json._id || !json.name) return
    actions.signin(json)
    SignUp({
      Id: json._id,
      Nome: json.name,
      Email: json.email,
    })
    if (source) {
      const action = source.match(/http|https/gi) ? open : navigate
      action(tokenize ? source.replace(TOKEN_MARKUP, json.token) : source)
    }
  }

  const handleSuccess = async (code: string) => {
    const request = await fetch('/api/proxy/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
    if (request.status === 200) {
      const json = await request.json()
      initSession(json)
    } else {
      toast({
        variant: 'destructive',
        title: 'Algo não saiu como esperado.',
        description: 'Houve um problema na autenticação com o Google.',
      })
    }
  }

  const handleError = ({
    name,
    message,
  }: { name: string; message: string }) => {
    logger(
      'ACCOUNT FORM SOCIAL SIGNUP',
      JSON.stringify({
        name,
        message,
      }),
    )
    toast({
      variant: 'destructive',
      title: 'Algo não saiu como esperado.',
      description: 'Houve um problema na comunicação com o Google.',
    })
  }

  const handleSubmit = async (data: z.infer<typeof AccountSignUpSchema>) => {
    try {
      loadingAtom.set('loading')
      const request = await fetch('/api/proxy/signup', {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data, null, 2),
      })

      const json = await request.json()
      loadingAtom.set(
        request.status === 422 || request.status === 409
          ? 'failed'
          : !source
            ? 'idle'
            : 'succeed',
      )
      if (request.status === 422) {
        toast({
          variant: 'destructive',
          title: 'Parâmetros inválidos.',
          description: 'Revise os campos informados.',
        })
      } else if (request.status === 409 && !json.ok) {
        form.setError('email', { message: 'E-mail já cadastrado' })
      } else {
        initSession(json)
      }
    } catch (error) {
      logger(
        'ACCOUNT FORM SIGNUP',
        JSON.stringify({
          name: error?.name,
          message: error?.message,
        }),
      )
      loadingAtom.set('failed')
      toast({
        variant: 'destructive',
        title: 'Algo não saiu como esperado.',
        description: 'Houve um problema com a sua requisição.',
      })
    }
  }

  const handleClickPassword = (ev: React.MouseEvent<HTMLElement>) => {
    ev.preventDefault()
    seePasswordAtom.set(!$seePassword)
  }

  const handleClickConfirmation = (ev: React.MouseEvent<HTMLElement>) => {
    ev.preventDefault()
    seePasswordConfirmationAtom.set(!$seePasswordConfirmation)
  }

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="p-2 pt-0 sm:p-6 sm:pt-0 space-y-4 min-w-72 sm:min-w-80">
            {!source && (
              <div className="space-y-1">
                <p className="font-semibold text-2xl">Crie uma conta</p>
                <p className="text-zinc-400 text-xs">
                  Insira alguns dados para acessar
                </p>
              </div>
            )}
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Nome completo"
                      {...field}
                      invalid={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="E-mail"
                      autoComplete="email"
                      {...field}
                      invalid={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Senha"
                      type={$seePassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      {...field}
                      value={field.value || ''}
                      invalid={!!fieldState.error}
                      right={
                        <Button
                          onClick={handleClickPassword}
                          variant="ghost"
                          size="icon"
                          className="rounded-full h-8 w-8"
                        >
                          {$seePassword ? (
                            <Eye className="h-3 w-3" />
                          ) : (
                            <EyeOff className="h-3 w-3" />
                          )}
                        </Button>
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirm"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Confirmação da senha"
                      autoComplete="new-password"
                      type={$seePasswordConfirmation ? 'text' : 'password'}
                      {...field}
                      value={field.value || ''}
                      invalid={!!fieldState.error}
                      right={
                        <Button
                          onClick={handleClickConfirmation}
                          variant="ghost"
                          size="icon"
                          className="rounded-full h-8 w-8"
                        >
                          {$seePasswordConfirmation ? (
                            <Eye className="h-3 w-3" />
                          ) : (
                            <EyeOff className="h-3 w-3" />
                          )}
                        </Button>
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-full p-2 sm:px-6 space-y-3">
            <ButtonLoading
              type="submit"
              className="w-full"
              loadingAtom={loadingAtom}
            >
              Cadastrar
            </ButtonLoading>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_KEY}>
              <ButtonGoogle
                variant="outline"
                className="w-full"
                onSuccess={handleSuccess}
                onFailed={handleError}
              >
                Cadastre-se com o Google
              </ButtonGoogle>
            </GoogleOAuthProvider>
          </div>
        </form>
      </Form>
    </div>
  )
}
